/**
 * RehearsalLayout — Rehearsal-only chrome.
 *
 * A row that says what persists, plus the controls that change it, atop a
 * full-width body Outlet. The "Save my work" CTA is a stub for wave 5
 * (signup hand-off); a parent can pass `onSave` to override the default
 * console.log.
 *
 * ## Why "Remember me" lives here
 *
 * This row already carries the page's only statement about persistence
 * ("Nothing here is saved"), which is the sentence the control makes
 * false. Putting the opt-in anywhere else would leave the page asserting
 * one thing at the top and doing another underneath, and would move the
 * consent decision away from where the visitor is already asking the
 * question.
 *
 * It is a button, and not a dismissible strip along the bottom of the
 * page, on purpose. That shape is the one every reader has trained
 * themselves to click away unread, so a consent notice wearing it gets
 * consented to by reflex. Which would defeat both of this control's jobs
 * at once, because pressing it is also the fastest available explanation
 * of what this product does.
 *
 * ## What this component cannot know
 *
 * Whether the visitor is remembered, until the server says so. There is
 * no `document.cookie` read here and there cannot be: the cookie is
 * HttpOnly. So the row renders the anonymous state and corrects itself,
 * rather than guessing — and it guesses "not remembered" rather than the
 * reverse, because the wrong guess in that direction is a missing button
 * and the wrong guess in the other is a **Forget me** offered to someone
 * with nothing to delete.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";

import {
  readRemembered,
  startRemembering,
  stopRemembering,
  UNKNOWN_REMEMBERED,
  type RememberedState,
} from "@/rehearsal/remember";

import { rehearsalLayoutCopy as C } from "./RehearsalLayout.copy";

export interface RehearsalLayoutProps {
  readonly onSave?: () => void;
}

/*
  Quiet, because neither of these is the primary action on this page.
  In solid brass the save CTA was the brightest thing on the visitor
  surface -- brighter than Send -- which asked a recruiter with ninety
  seconds to save work they had not done yet. #66 says one primary
  action, and it is the one that talks to Margo. Remember me inherits
  the same restraint for the same reason: it is worth offering and it
  is not worth interrupting a conversation for.
*/
const CONTROL_STYLE: React.CSSProperties = {
  padding: "6px 14px",
  fontSize: "0.875rem",
  background: "transparent",
  color: "var(--brass-cue)",
  border: "1px solid var(--brass-tarnish)",
  borderRadius: "var(--radius)",
};

export default function RehearsalLayout({
  onSave,
}: RehearsalLayoutProps = {}): React.ReactElement {
  const [state, setState] = useState<RememberedState>(UNKNOWN_REMEMBERED);
  /** True once the server has answered, so the note can carry real facts. */
  const [answered, setAnswered] = useState(false);
  const [pending, setPending] = useState(false);
  const live = useRef(true);

  useEffect(() => {
    live.current = true;
    readRemembered()
      .then((next) => {
        if (!live.current) return;
        setState(next);
        setAnswered(true);
      })
      .catch(() => {
        // A page that cannot reach the server shows the row it showed
        // before this feature existed. No error chrome: the visitor came
        // to talk to someone, and a banner about a cookie endpoint is a
        // worse first screen than a quiet one. The control stays
        // pressable, and a press that also fails leaves the row alone.
      });
    return () => {
      live.current = false;
    };
  }, []);

  const handleSave = (): void => {
    if (onSave) {
      onSave();
      return;
    }
    if (typeof console !== "undefined") {
      console.log("[rehearsal] save-my-work CTA clicked (stub)");
    }
  };

  const handleToggle = useCallback((): void => {
    if (pending) return;
    setPending(true);
    const call = state.remembered ? stopRemembering : startRemembering;
    call()
      .then((next) => {
        if (!live.current) return;
        // Set from the response, never from the intention. A forget that
        // failed has to leave the row saying she still remembers,
        // because she does. A page that optimistically claimed otherwise
        // would be lying about the single thing this control promises,
        // and would look identical to one that worked.
        setState(next);
        setAnswered(true);
      })
      .catch(() => {
        /* Leave the row as it was. See above. */
      })
      .finally(() => {
        if (live.current) setPending(false);
      });
  }, [pending, state.remembered]);

  const remembered = state.remembered;
  const note = remembered ? C.forgetNote(state.expires_in_days) : C.rememberNote;

  return (
    <section
      data-testid="layout-rehearsal"
      className="bg-rehearsal-taupe rounded-ledger-line"
      style={{
        display: "flex", flexDirection: "column", gap: "1rem",
        padding: "1rem 1.25rem", border: "1px solid var(--rehearsal-edge)",
      }}
    >
      <div
        data-testid="rehearsal-bar"
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: "0.75rem 1rem",
          borderBottom: "1px solid var(--ledger-line)", paddingBottom: "0.75rem",
        }}
      >
        <span
          data-testid="rehearsal-pill"
          /*
            Prose, not monospace. A mono pill reads as a system status
            badge; this is a sentence to a person, and it sits above a
            conversation rather than above a console.

            It changes with the state because it is the page's claim
            about persistence, and "nothing here is saved" stops being
            true the moment someone presses the button next to it.
          */
          className="text-paper-cream-faint"
          style={{ padding: "4px 0", fontSize: "0.781rem" }}
        >{remembered ? C.rememberedPill : C.anonymousPill}</span>

        <div
          style={{
            display: "flex", alignItems: "center",
            flexWrap: "wrap", gap: "0.5rem 0.75rem",
          }}
        >
          {/*
            Beside the control, not behind a link. "What does this do" is
            the question a visitor has at the moment they are deciding,
            and a policy page answers it after they have already decided.

            Held back until the server has answered, because half of what
            it says is a number the server owns.
          */}
          {answered ? (
            <span
              data-testid="rehearsal-remember-note"
              className="text-paper-cream-faint"
              style={{ fontSize: "0.781rem" }}
            >{note}</span>
          ) : null}
          <button
            type="button"
            data-testid="rehearsal-remember-toggle"
            onClick={handleToggle}
            disabled={pending}
            aria-pressed={remembered}
            className="font-sohne-display text-paper-cream tracking-staff"
            style={{
              ...CONTROL_STYLE,
              cursor: pending ? "default" : "pointer",
              opacity: pending ? 0.6 : 1,
            }}
          >{remembered ? C.forgetAction : C.rememberAction}</button>
          <button
            type="button"
            data-testid="rehearsal-save-cta"
            onClick={handleSave}
            className="font-sohne-display text-paper-cream tracking-staff"
            style={{ ...CONTROL_STYLE, cursor: "pointer" }}
          >{C.saveCta}</button>
        </div>
      </div>
      <div style={{ width: "100%" }}><Outlet /></div>
    </section>
  );
}
